import React from 'react'
import { Field, Form, Formik } from 'formik'
import { FormModalScreen } from 'components/FormModal'
import { CkEditorStyledField } from 'civic-champs-shared/formik/components/CkEditorStyledField'
import RadioGroupFormField from 'components/RadioGroupFormField'
import { AttachQuestionnaires } from 'messages/components/AttachQuestionnaires'
import { useAddEditEventStyles } from 'Event/hooks/useAddEditEventStyles'
import yup from 'civic-champs-shared/utils/yup'
import { hasRichTextContent } from 'Event/helpers/rich-text'

export interface EventAutomatedMessagesScreenValues {
  registrationMessage: string
  instructions: string
  followUpEnabled: boolean
  followUpMessage: string
  followUpQuestionnaireIds: number[]
}

export interface EventAutomatedMessagesScreenProps {
  values: EventAutomatedMessagesScreenValues
  onSubmit: (values: EventAutomatedMessagesScreenValues, asDraft?: boolean) => void
  onBack: (values: EventAutomatedMessagesScreenValues) => void
  isNewEvent: boolean
  isDraftable: boolean
}

const schema = yup
  .object({
    registrationMessage: yup.string().nullable().notRequired(),
    instructions: yup.string().nullable().notRequired(),
    followUpMessage: yup.string().nullable().notRequired(),
    followUpEnabled: yup.boolean(),
    followUpQuestionnaireIds: yup.array().of(yup.number()),
  })
  .test('followUpContent', 'Enter a follow-up message or attach at least one questionnaire', value => {
    if (!value?.followUpEnabled) {
      return true
    }
    return hasRichTextContent(value.followUpMessage) || (value.followUpQuestionnaireIds?.length ?? 0) > 0
  })

export const EventAutomatedMessagesScreen = ({
  values,
  onSubmit,
  onBack,
  isNewEvent,
  isDraftable,
}: EventAutomatedMessagesScreenProps) => {
  const classes = useAddEditEventStyles()
  const [asDraft, setAsDraft] = React.useState(false)

  return (
    <Formik
      initialValues={values}
      validationSchema={schema}
      onSubmit={submitted => onSubmit(submitted, asDraft)}
      enableReinitialize
      isInitialValid={!isNewEvent}
    >
      {({ submitForm, isSubmitting, values: formValues }) => (
        <Form>
          <FormModalScreen
            onBack={() => onBack(formValues)}
            onNext={submitForm}
            onDraft={
              isDraftable
                ? () => {
                    setAsDraft(true)
                    submitForm()
                  }
                : undefined
            }
            draftText={isDraftable ? 'Finish Later' : undefined}
            nextText="Save"
            backText="Back"
            disabled={isSubmitting && isDraftable}
          >
            <div className={classes.section}>
              <Field
                name="registrationMessage"
                label="Registration"
                placeholder="Enter additional registration information. This will be sent to volunteers when they register."
                component={CkEditorStyledField}
              />
            </div>
            <div className={classes.section}>
              <Field
                name="instructions"
                label="Day-Before Reminder"
                placeholder="Enter Day-Before Reminder instructions. This will be sent to registrants the day before they are scheduled to volunteer."
                component={CkEditorStyledField}
              />
            </div>
            <div className={classes.section}>
              <span className={classes.sectionTitle}>Follow-Up</span>
              <RadioGroupFormField
                valueIsBoolean
                name="followUpEnabled"
                disabled={isSubmitting}
                options={[
                  {
                    label: 'Do not send a follow-up message after volunteering',
                    value: false,
                  },
                  {
                    label: 'Send a follow-up message to all attendees',
                    value: true,
                  },
                ]}
              />
              {formValues.followUpEnabled && (
                <>
                  <Field
                    name="followUpMessage"
                    label="Follow-Up"
                    placeholder="Enter a follow-up/thank you message. This will be sent the day after the event concludes to volunteers who attended."
                    component={CkEditorStyledField}
                  />
                  <Field name="followUpQuestionnaireIds" component={AttachQuestionnaires} />
                </>
              )}
            </div>
          </FormModalScreen>
        </Form>
      )}
    </Formik>
  )
}
